import { exploreSchemaItemList } from '@/templates/Landing/seoLandings/exploreSchema';
import { ENV } from '@/constants';

export const schemaObject_ImageExtender = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/image-extender',
			name: 'AI Image Extender | Lift',
			description:
				"Expand your images effortlessly with Lift's AI Image Extender. Seamlessly add new content beyond the original borders in seconds. Try it now—no watermark.",
			url: 'https://liftapp.ai/image-extender',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/image-extender#product' },
				{ '@id': 'https://liftapp.ai/image-extender#howto' },
				{ '@id': 'https://liftapp.ai/image-extender#benefits' },
				{ '@id': 'https://liftapp.ai/image-extender#faq' },
				{ '@id': 'https://liftapp.ai/image-extender#why-lift' },
				{ '@id': 'https://liftapp.ai/image-extender#explore-tools' },
				{ '@id': 'https://liftapp.ai/image-extender#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/image-extender#product',
			name: 'AI Image Extender',
			description:
				'Extend your images beyond their borders with AI in seconds. No watermark. Create yours with Lift now.',
			url: 'https://liftapp.ai/image-extender',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: 4.8,
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/image-extender#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: 'Choose a photo from your device or take one using Lift’s app',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let AI uncrop it',
					text: 'Our AI analyzes your photo, understands its composition, and seamlessly extends the background while keeping every detail natural',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-extender#benefits',
			name: 'Who can benefit from AI Image Extender?',
			description:
				'The AI Image Expander is perfect for anyone who wants to fix awkwardly cropped photos, social media users, content creators, friends and couples, and e-commerce sellers.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'The AI Image Expander is perfect for anyone who wants to fix awkwardly cropped photos',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Social media users can make their posts fit any format — from Instagram Stories to YouTube thumbnails',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Content creators can expand scenes for posters, banners, or marketing visuals',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Friends and couples can create fun, shareable anime portraits together',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'E-commerce sellers can give products more space to breathe in photos without retakes',
				},
				{
					'@type': 'ListItem',
					position: 6,
					name: 'This feature saves time, effort, and money — a must-have for both personal and professional use',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.ab1bf183.webp',
				name: 'Three women walking on the beach',
				description:
					'An image of three women holding hands and walking on a beach. A dotted frame with arrows suggests the use of a tool for editing or extending the image.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/image-extender#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Image Extender?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Image Extender is an online tool that extends your photos beyond their original borders — perfect for uncropping, outpainting, or adapting images to new formats.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Image Extender work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Our AI understands your photo’s colors, textures, and perspective, then generates realistic extensions that blend seamlessly. Everything runs in the cloud — no manual editing required.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep the original look of my photo?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. The AI preserves your photo’s style, lighting, and composition, so the expanded area looks like it was always part of the picture.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-extender#why-lift',
			name: 'Why Lift’s AI Image Extender stands out',
			description:
				"Lift's AI Image Extender provides professional results, AI accuracy, cross-platform support, and is budget-friendly.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Professional results',
					description:
						'Achieve studio-quality images without the need for complex tools',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI accuracy',
					description:
						'Smart algorithms deliver enhancements tailored to your specific image',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly editing services with Lift’s affordable solution',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyLift.bab0bb04.webp',
				name: 'An image of a cosmetic bottle',
				description:
					'An image of a cosmetic bottle and a coconut, with a dotted frame suggesting a tool for extending or editing the image.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-extender#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/image-extender#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Object Remover',
					item: 'https://liftapp.ai/image-extender',
				},
			],
		},
	],
};
